import { useActivePageId, useDeletePageProducts, usePageProducts } from "../../../data";
import type { GetContextMenuSection } from "./helpers";
import { colParams } from "../../utils";
import { getIcon } from "./icons";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";
import type { getMenuItemContext } from "./helpers";
import { useGridSettingsSelect } from "../../../data/grid-settings";
import { useConfirmation } from "../../components/hooks/useConfirmation";
import { useUserSubscriptionTier } from "../../../data/user";

function getSelectedColumnIds(ctx: ReturnType<typeof getMenuItemContext>): string[] {
  const ids = Array.from(
    new Set(
      (ctx.selectedRange || [])
        .flat()
        .map((x) => x.columnId)
        .concat((ctx.rangeSelection || []).map((x) => x.columnId)),
    ),
  );
  if (ids.length === 0 && ctx.columnId) ids.push(ctx.columnId);
  return ids;
}

function computeSelectionMeta(
  selectedColumnIds: string[],
  products: PageProductsFragmentFragment[],
): {
  selectedIds: string[];
  hasShadow: boolean;
  plural: boolean;
  name: string;
} {
  const selectedRecords = products.filter((p) => selectedColumnIds.includes(p.grid_id || p.id || ""));
  const selectedIds = selectedRecords.map((p) => p.id).filter(Boolean);
  const hasShadow = selectedRecords.some((p) => p.column_type !== "product");
  const plural = selectedColumnIds.length > 1;
  const name = hasShadow ? (plural ? "Remove Columns" : "Remove Column") : plural ? "Remove Curves" : "Remove Curve";
  return { selectedIds, hasShadow, plural, name };
}

export const useGetMenuItemsRemoveColumn = (): GetContextMenuSection => {
  const { disabledFeatures } = useUserSubscriptionTier();
  const remove = useDeletePageProducts();
  const pageProducts = usePageProducts(useActivePageId());
  const dontShowSingleWarning = useGridSettingsSelect((s) => s?.dont_show_remove_curve_confirmation_dialog);
  const dontShowMultipleWarning = useGridSettingsSelect((s) => s?.dont_show_remove_multiple_curves_confirmation_dialog);
  const confirm = useConfirmation();

  const openConfirm = (ids: string | string[]) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    const selected = (pageProducts.data || []).filter((pp: PageProductsFragmentFragment) => idArray.includes(pp.id));
    const hasShadow = selected.some((pp: PageProductsFragmentFragment) => pp.column_type !== "product");
    const count = idArray.length;
    const plural = count > 1;

    const title = hasShadow
      ? plural
        ? `Remove ${count} Columns from Page?`
        : "Remove Column from Page?"
      : plural
        ? `Remove ${count} Curves from Page?`
        : "Remove Curve from Page?";

    const confirmButtonText = `Remove ${hasShadow ? (plural ? "Columns" : "Column") : plural ? "Curves" : "Curve"}`;

    const message = hasShadow
      ? plural
        ? `You're about to remove ${count} selected Columns from your Page. This action can't be undone.`
        : "You're about to remove the selected Column from your Page. This action can't be undone."
      : plural
        ? `You're about to remove ${count} selected Curves from your Page. This action can't be undone.`
        : "You're about to remove the selected Curve from your Page. This action can't be undone.";

    confirm({
      title,
      message,
      confirmButtonText,
      variant: "danger",
      dontShowAgainKey: plural
        ? "dont_show_remove_multiple_curves_confirmation_dialog"
        : "dont_show_remove_curve_confirmation_dialog",
      onConfirm: () => {
        idArray.forEach((id: string) => {
          if (id) remove.mutate(id);
        });
      },
    });
  };

  const handleRemove = (id: string) => {
    if (id) remove.mutateAsync(id);
  };

  return (ctx) => {
    const product = pageProducts.data?.find((p: PageProductsFragmentFragment) => p.grid_id === ctx.columnId);
    const id = product?.id || colParams(ctx.api.getColumn(ctx.columnId))?.gridId;
    if (ctx.isProduct) {
      const selectedColumnIds = Array.from(
        new Set(
          (ctx.selectedRange || [])
            .flat()
            .map((x) => x.columnId)
            .concat((ctx.rangeSelection || []).map((x) => x.columnId)),
        ),
      );
      if (selectedColumnIds.length === 0 && ctx.columnId) {
        selectedColumnIds.push(ctx.columnId);
      }
      const { selectedIds, name } = computeSelectionMeta(selectedColumnIds, pageProducts.data || []);

      return [
        {
          name,
          disabled: !!disabledFeatures?.removeCurve,
          tooltip: disabledFeatures?.removeCurve
            ? "Please upgrade your subscription to access this feature"
            : undefined,
          icon: getIcon("remove"),
          action: () => {
            const isMultiple = selectedIds.length > 1;
            const skipWarning = isMultiple ? dontShowMultipleWarning.data : dontShowSingleWarning.data;

            if (skipWarning) {
              if (isMultiple) {
                selectedIds.forEach((removeId: string) => {
                  if (removeId) remove.mutate(removeId);
                });
              } else if (id) {
                remove.mutate(id);
              }
              return;
            }
            if (isMultiple) {
              openConfirm(selectedIds);
              return;
            }
            if (id) openConfirm(id);
          },
        },
      ];
    }
    if (ctx.isShadowCurve) {
      return [
        {
          name: (() => {
            const selectedColumnIds = getSelectedColumnIds(ctx);
            const { name } = computeSelectionMeta(selectedColumnIds, pageProducts.data || []);
            return name;
          })(),
          icon: getIcon("remove"),
          action: () => {
            const selectedColumnIds = getSelectedColumnIds(ctx);
            const { selectedIds } = computeSelectionMeta(selectedColumnIds, pageProducts.data || []);
            const isMultiple = selectedIds.length > 1;
            const skipWarning = isMultiple ? dontShowMultipleWarning.data : dontShowSingleWarning.data;

            if (skipWarning) {
              if (selectedIds.length > 0) {
                selectedIds.forEach((removeId: string) => {
                  if (removeId) remove.mutate(removeId);
                });
              } else if (id) {
                handleRemove(id);
              }
              return;
            }

            if (isMultiple) {
              openConfirm(selectedIds);
              return;
            }
            if (id) {
              handleRemove(id);
            }
          },
        },
      ];
    }
    return [];
  };
};
